/**
 * HandoffView — Owner-only export panel for migrating the agent runtime
 * layer to Relevance AI (or any other platform).
 *
 * Lists every doc in /app/docs/ (Markdown + JSON specs + tarball/zip
 * bundles) with one-click download links. All downloads route through
 * GET /api/crm/handoff/download/{filename} which is owner-gated.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Download,
  FileArchive,
  FileCode2,
  FileText,
  Loader2,
  PackageOpen,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { crmApi } from "@/lib/ownerClient";

const FILE_BLURBS = {
  "README.md": "Table of contents + suggested read order.",
  "HANDOFF.md":
    "Master spec — architecture, business rules, persona/runner overview.",
  "manifest.json":
    "Machine-readable summary for AI ingestion (versions, counts, phase status).",
  "ENV_VARS.md":
    "Every env var the backend needs + where to obtain each key.",
  "AGENTS_SPEC.md":
    "The 11 personas + 12 swarm runners with cadences & system prompts.",
  "API_REFERENCE.md":
    "All 223 REST endpoints with sample request/response payloads.",
  "DB_SCHEMA.md":
    "All 55 MongoDB collections with field shapes + recommended indexes.",
  "DEPLOY.md":
    "Step-by-step self-host on Render + MongoDB Atlas + Vercel.",
  "RELEVANCE_AI_INTEGRATION.md":
    "★ The wiring guide — HTTP tools, agent prompts, cron schedules, phased migration.",
  "blackridge-handoff-bundle.zip":
    "All docs in one zip (Windows-friendly).",
  "blackridge-handoff-bundle.tar.gz":
    "All docs in one tarball (Linux/Mac).",
};

const READ_ORDER = [
  "README.md",
  "HANDOFF.md",
  "manifest.json",
  "ENV_VARS.md",
  "DEPLOY.md",
  "API_REFERENCE.md",
  "DB_SCHEMA.md",
  "AGENTS_SPEC.md",
  "RELEVANCE_AI_INTEGRATION.md",
];

function iconForFile(name) {
  if (name.endsWith(".zip") || name.endsWith(".tar.gz") || name.endsWith(".gz")) {
    return FileArchive;
  }
  if (name.endsWith(".json")) return FileCode2;
  return FileText;
}

function sortByReadOrder(files) {
  const docs = files.filter((f) => !f.is_bundle);
  const bundles = files.filter((f) => f.is_bundle);
  docs.sort((a, b) => {
    const ai = READ_ORDER.indexOf(a.name);
    const bi = READ_ORDER.indexOf(b.name);
    if (ai === -1 && bi === -1) return a.name.localeCompare(b.name);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  return { docs, bundles };
}

export default function HandoffView() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({ files: [], doc_count: 0, bundle_count: 0 });

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await crmApi.listHandoffFiles();
      setData(res || { files: [] });
    } catch (exc) {
      setError(exc.message || "Failed to load handoff files");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const { docs, bundles } = useMemo(
    () => sortByReadOrder(data.files || []),
    [data.files],
  );

  const totalSize = useMemo(
    () => (data.files || []).reduce((acc, f) => acc + (f.size_bytes || 0), 0),
    [data.files],
  );

  const downloadFile = (name) => {
    // Direct browser navigation so the auth cookie travels and the response
    // triggers the browser's native download UI.
    const link = document.createElement("a");
    link.href = crmApi.handoffDownloadUrl(name);
    link.download = name;
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const lastModified = useMemo(() => {
    if (!data.files || !data.files.length) return null;
    const newest = [...data.files].sort((a, b) =>
      (b.modified_at || "").localeCompare(a.modified_at || ""),
    )[0];
    return newest?.modified_at || null;
  }, [data.files]);

  return (
    <div className="space-y-6 p-6 max-w-6xl" data-testid="handoff-view">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-100 flex items-center gap-2">
            <PackageOpen className="h-6 w-6 text-amber-400" />
            Migration Handoff Bundle
          </h1>
          <p className="text-sm text-zinc-400 max-w-2xl">
            Complete export of the CRM architecture, agent specs, and
            integration playbook — designed to be ingested by Relevance AI (or
            any other AI development platform).
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          disabled={loading}
          data-testid="handoff-refresh"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {/* Status strip */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400">
        <Badge variant="outline" className="border-emerald-700/60 text-emerald-300">
          <ShieldCheck className="h-3 w-3 mr-1" />
          Owner-gated
        </Badge>
        <span>
          {data.doc_count || 0} docs · {data.bundle_count || 0} bundles ·{" "}
          {Math.round(totalSize / 1024)} KB total
        </span>
        {lastModified && (
          <span className="text-zinc-500">
            Last generated:{" "}
            {new Date(lastModified).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </span>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-red-900/50 bg-red-950/40 text-red-300 px-4 py-3 text-sm" data-testid="handoff-error">
          {error}
        </div>
      )}

      {/* Bundles (download all) */}
      {bundles.length > 0 && (
        <Card className="border-amber-900/40 bg-gradient-to-br from-amber-950/20 to-zinc-950/60" data-testid="handoff-bundles-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-amber-200 text-base flex items-center gap-2">
              <FileArchive className="h-4 w-4" />
              Download everything as a single archive
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {bundles.map((f) => {
              const Icon = iconForFile(f.name);
              const isZip = f.name.endsWith(".zip");
              return (
                <button
                  key={f.name}
                  type="button"
                  onClick={() => downloadFile(f.name)}
                  className="group flex items-center gap-3 rounded-md border border-zinc-800 hover:border-amber-700/60 bg-zinc-950/40 hover:bg-amber-950/20 transition-colors p-4 text-left"
                  data-testid={`handoff-bundle-${isZip ? "zip" : "tarball"}`}
                >
                  <div className="rounded-md bg-amber-900/30 p-2 group-hover:bg-amber-800/40">
                    <Icon className="h-5 w-5 text-amber-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-100 truncate">
                      {f.name}
                    </div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {f.size_human}
                      {isZip ? " · Recommended for Windows" : " · Recommended for Linux/Mac"}
                    </div>
                  </div>
                  <Download className="h-4 w-4 text-zinc-500 group-hover:text-amber-300" />
                </button>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Individual docs */}
      <Card className="border-zinc-800 bg-zinc-950/60" data-testid="handoff-docs-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-zinc-100 text-base">
            Individual documents
          </CardTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Ordered by recommended read sequence. Click any row to download.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {loading && !docs.length ? (
            <div className="p-8 text-center text-zinc-500 text-sm">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              Loading manifest…
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="w-12 text-zinc-500">#</TableHead>
                  <TableHead className="text-zinc-400">File</TableHead>
                  <TableHead className="text-zinc-400">Description</TableHead>
                  <TableHead className="text-zinc-400 text-right w-24">
                    Size
                  </TableHead>
                  <TableHead className="text-zinc-400 text-right w-32">
                    Action
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {docs.map((f, idx) => {
                  const Icon = iconForFile(f.name);
                  const blurb = FILE_BLURBS[f.name] || "";
                  const isStar = f.name === "RELEVANCE_AI_INTEGRATION.md";
                  return (
                    <TableRow
                      key={f.name}
                      className="border-zinc-800 hover:bg-zinc-900/40 cursor-pointer"
                      onClick={() => downloadFile(f.name)}
                      data-testid={`handoff-doc-${f.name.replace(/\./g, "_")}`}
                    >
                      <TableCell className="text-zinc-500 font-mono text-xs">
                        {idx + 1}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4 text-zinc-500 shrink-0" />
                          <span
                            className={`text-sm font-medium ${
                              isStar ? "text-amber-200" : "text-zinc-100"
                            }`}
                          >
                            {f.name}
                          </span>
                          {isStar && (
                            <Badge
                              variant="outline"
                              className="border-amber-700/60 text-amber-300 text-[10px] uppercase tracking-wider"
                            >
                              key doc
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-zinc-400 max-w-md">
                        {blurb}
                      </TableCell>
                      <TableCell className="text-right text-xs text-zinc-500 font-mono">
                        {f.size_human}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            downloadFile(f.name);
                          }}
                          data-testid={`handoff-download-${f.name.replace(/\./g, "_")}`}
                        >
                          <Download className="h-3.5 w-3.5 mr-1.5" />
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Separator className="bg-zinc-800" />

      {/* Footer guidance */}
      <Card className="border-zinc-800 bg-zinc-950/40">
        <CardContent className="p-5 text-xs text-zinc-400 space-y-2">
          <p>
            <span className="text-zinc-300 font-medium">For Relevance AI migration:</span>{" "}
            Start with <span className="text-amber-300">RELEVANCE_AI_INTEGRATION.md</span> — it
            contains every HTTP Tool definition, agent prompt, and cron schedule
            needed to recreate the 12-runner swarm on Relevance AI&apos;s platform.
          </p>
          <p>
            <span className="text-zinc-300 font-medium">For a new dev / contractor:</span> Hand
            them the zip; have them read in the order listed above. The
            companion AI on their end (Claude / Cursor / etc.) can ingest the
            full bundle as context.
          </p>
          <p>
            <span className="text-zinc-300 font-medium">Internal-only:</span> These docs
            describe internal architecture and integration credentials by name
            (no live secrets are embedded). Do not share the zip publicly.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
